import smsForm from './smsForm.js';

/**
 * SMS 발송 단독 팝업창 컴포넌트 — view/sms/popup.html 전용.
 *
 * <p>smsForm 의 base(remain/hasAuth/phones/message/bytes/isLms/parseInput/submit ...) 를
 * 그대로 가져오고, 다음 기능을 확장한다:
 *   1) 발신번호 미등록 차단 모달(blockerOpen)
 *   2) 이용법 모달(helpOpen)
 *   3) 우측 주소록 — 의뢰인/공유업무자 sub-tab + 검색 + 체크박스 + "수신자에 추가"
 *   4) data-txt-param / data-receiver-param 외부 텍스트 자동 채움
 *
 * <p>합성 패턴: smsForm 이 반환한 객체를 그대로 받아 popup 전용 필드/메서드를 추가한다.
 * spread/Object.assign 은 getter(bytes/isLms)를 일반 속성으로 변환해버리므로 사용 금지 —
 * 같은 객체 인스턴스에 직접 프로퍼티/메서드를 추가하고, popup 전용 computed 는 defineProperty 로 부착.
 */
export default () => {
    const o = smsForm();

    // ── popup 전용 상태 ───────────────────────────────────────
    o.blockerOpen = false;
    o.helpOpen = false;
    o.addressTab = 'client';
    o.clientList = [];
    o.userList = [];
    o.clientSearch = '';
    o.userSearch = '';
    o.checkedClient = new Set();
    o.checkedUser = new Set();
    o.lmsLimitWarned = false;

    // ── 전송결과 탭 상태 (lazy-load) ────────────────────────
    o.activeTab = 'send';                                   // 'send' | 'result'
    o.results = [];
    o.pageInfo = { page: 1, size: 10, total: 0, totalPages: 0 };
    o.search = { smsType: 'all', resultStatus: 'all', senderNumber: '', receiverNumber: '' };
    o.resultsLoaded = false;
    o.resultsLoading = false;
    o.detailOpen = false;
    o.detail = {};

    // ── init override — base init() 호출 후 popup 전용 hydration ──
    const baseInit = o.init;
    o.init = function () {
        // base init 은 발신번호 select 의 option 개수로 hasAuth 추정.
        // popup 는 서버에서 정확히 알려주므로 data-has-auth 로 덮어쓴다.
        baseInit.call(this);

        const root = this.$root;
        const explicit = root?.dataset?.hasAuth;
        if (explicit === 'true' || explicit === 'false') {
            this.hasAuth = (explicit === 'true');
        }
        this.blockerOpen = !this.hasAuth;

        // 주소록 JSON 두 개 — <script type="application/json"> 안의 직렬화 문자열 파싱
        try {
            this.clientList = JSON.parse(
                document.getElementById('clientHpJson')?.textContent || '[]');
        } catch (e) {
            console.warn('[smsPopupForm] clientHpJson parse failed', e);
            this.clientList = [];
        }
        try {
            this.userList = JSON.parse(
                document.getElementById('lawdeskUserSmsJson')?.textContent || '[]');
        } catch (e) {
            console.warn('[smsPopupForm] lawdeskUserSmsJson parse failed', e);
            this.userList = [];
        }

        // 부모창에서 넘긴 외부 텍스트/수신자 자동 채움
        const txt = root?.dataset?.txtParam || '';
        if (txt) {
            this.message = txt;
        }
        const rcv = root?.dataset?.receiverParam || '';
        if (rcv) {
            this.rawInput = rcv;
            this.parseInput();
        }

        // 메시지 2000 bytes 상한 — 입력/페이스트/재전송 주입 모두 watcher 로 일괄 절단
        this.$watch('message', () => this.enforceMessageLimit());
        this.enforceMessageLimit();
    };

    // ── 메시지 byte 상한 강제 ────────────────────────────────
    // 2000 bytes 초과 시 한글 단위로 안전하게 절단 + toast 1회 경고
    o.enforceMessageLimit = function () {
        if (this.bytes <= 2000) {
            if (this.lmsLimitWarned) this.lmsLimitWarned = false;
            return;
        }
        const chars = Array.from(this.message);
        let n = 0;
        let cutAt = chars.length;
        for (let i = 0; i < chars.length; i++) {
            const add = (chars[i].charCodeAt(0) > 127) ? 2 : 1;
            if (n + add > 2000) { cutAt = i; break; }
            n += add;
        }
        this.message = chars.slice(0, cutAt).join('');
        if (!this.lmsLimitWarned) {
            this.toastWarn('LMS 는 2000 bytes까지 허용됩니다');
            this.lmsLimitWarned = true;
        }
    };

    // ── 검색 필터 (computed) — Alpine getter 로 부착 ───────────
    Object.defineProperty(o, 'filteredClients', {
        get() {
            const q = (this.clientSearch || '').trim().toLowerCase();
            if (!q) return this.clientList;
            return this.clientList.filter(c =>
                (c.cname || '').toLowerCase().includes(q) ||
                (c.mobile || '').includes(q));
        },
        enumerable: true,
        configurable: true,
    });
    Object.defineProperty(o, 'filteredUsers', {
        get() {
            const q = (this.userSearch || '').trim().toLowerCase();
            if (!q) return this.userList;
            return this.userList.filter(u =>
                (u.ubName || '').toLowerCase().includes(q) ||
                (u.ubTel || '').includes(q));
        },
        enumerable: true,
        configurable: true,
    });

    // ── 의뢰인 체크 토글 ─────────────────────────────────────
    o.isClientChecked = function (seq) {
        return this.checkedClient.has(seq);
    };
    o.toggleClient = function (seq) {
        const next = new Set(this.checkedClient);
        if (next.has(seq)) next.delete(seq);
        else next.add(seq);
        this.checkedClient = next;   // Set 인스턴스 교체로 reactivity 트리거
    };
    o.toggleAllClients = function () {
        const visible = this.filteredClients;
        if (!visible.length) return;
        const allChecked = visible.every(c => this.checkedClient.has(c.seq));
        const next = new Set(this.checkedClient);
        if (allChecked) visible.forEach(c => next.delete(c.seq));
        else visible.forEach(c => next.add(c.seq));
        this.checkedClient = next;
    };

    // ── 공유업무자 체크 토글 (key = "ubName|ubTel") ─────────
    o.isUserChecked = function (key) {
        return this.checkedUser.has(key);
    };
    o.toggleUser = function (key) {
        const next = new Set(this.checkedUser);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        this.checkedUser = next;
    };
    o.toggleAllUsers = function () {
        const visible = this.filteredUsers;
        if (!visible.length) return;
        const keys = visible.map(u => u.ubName + '|' + u.ubTel);
        const allChecked = keys.every(k => this.checkedUser.has(k));
        const next = new Set(this.checkedUser);
        if (allChecked) keys.forEach(k => next.delete(k));
        else keys.forEach(k => next.add(k));
        this.checkedUser = next;
    };

    // ── 전송결과 탭 — lazy-load & 페이징 & 모달 ─────────────
    o.switchToResultTab = function () {
        this.activeTab = 'result';
        if (!this.resultsLoaded) {
            this.loadResults(1);
        }
    };

    o.loadResults = async function (page) {
        const totalPages = this.pageInfo.totalPages;
        if (page < 1) return;
        if (totalPages > 0 && page > totalPages) return;

        this.resultsLoading = true;
        const params = new URLSearchParams({
            page: String(page),
            size: String(this.pageInfo.size),
            smsType: this.search.smsType,
            resultStatus: this.search.resultStatus,
            senderNumber: this.search.senderNumber || '',
            receiverNumber: this.search.receiverNumber || '',
        });
        try {
            const res = await fetch('/sms/api/results?' + params.toString(), {
                headers: { 'Accept': 'application/json' },
            });
            if (!res.ok) {
                console.warn('[smsPopupForm] /sms/api/results 응답 실패', res.status);
                this.toastWarn?.('전송결과 조회에 실패했습니다.');
                return;
            }
            const data = await res.json();
            this.results = Array.isArray(data.content) ? data.content : [];
            this.pageInfo = {
                page: data.page || 1,
                size: data.size || this.pageInfo.size,
                total: data.total || 0,
                totalPages: data.totalPages || 0,
            };
            this.resultsLoaded = true;
        } catch (e) {
            console.error('[smsPopupForm] loadResults error', e);
            this.toastWarn?.('전송결과 조회 중 오류가 발생했습니다.');
        } finally {
            this.resultsLoading = false;
        }
    };

    o.pageWindow = function () {
        // 5개씩 묶음 페이지 윈도우 — 현재 page 가 6 이면 6~10 표시, 11 이면 11~15
        const cur = this.pageInfo.page;
        const tot = this.pageInfo.totalPages;
        if (tot <= 0) return [];
        const block = Math.floor((cur - 1) / 5);
        const start = block * 5 + 1;
        const end = Math.min(start + 4, tot);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    o.openDetail = async function (seq) {
        try {
            const res = await fetch('/sms/api/results/' + seq, {
                headers: { 'Accept': 'application/json' },
            });
            if (!res.ok) {
                this.toastWarn?.('상세 조회에 실패했습니다.');
                return;
            }
            this.detail = await res.json();
            this.detailOpen = true;
        } catch (e) {
            console.error('[smsPopupForm] openDetail error', e);
            this.toastWarn?.('상세 조회 중 오류가 발생했습니다.');
        }
    };

    o.applyRetry = function () {
        const d = this.detail || {};
        this.message = d.smsContent || '';
        this.subject = d.smsTitle || '';
        this.forceLms = d.smsType === 'lms';
        // 발신번호 — authList 에 동일 번호가 있으면 자동 선택 (없으면 사용자가 다시 선택)
        if (d.smsSenderNumber) {
            const opts = Array.from(
                document.querySelectorAll('.sms-popup select[x-model="senderNumber"] option'));
            const matched = opts.find(o => o.value === d.smsSenderNumber);
            if (matched) this.senderNumber = d.smsSenderNumber;
        }
        // phones 직접 주입 — rawInput 우회 (이미 검증된 번호)
        const name = d.smsReceiverName || '';
        const phone = d.smsReceiverNumber || '';
        const display = name ? `${name}|${phone}` : phone;
        this.phones = phone ? [{ name, phone, display, valid: true }] : [];
        this.rawInput = '';
        this.detailOpen = false;
        this.activeTab = 'send';
    };

    o.formatRegdate = function (s) {
        // DB DATETIME 문자열 (yyyy-MM-dd HH:mm:ss) → "yy.MM.dd HH:mm"
        if (!s) return '';
        const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
        if (!m) return s;
        return `${m[1].slice(2)}.${m[2]}.${m[3]} ${m[4]}:${m[5]}`;
    };

    o.statusLabel = function (r) {
        if (!r) return '';
        if (r.smsSendResultStatus === 'S') return '성공';
        if (r.smsSendResultStatus === 'F') {
            return r.info1 ? `실패 (${r.info1})` : '실패';
        }
        return '대기';
    };

    // ── 체크된 항목을 수신자 칩에 추가 ──────────────────────
    o.addCheckedToRecipients = function () {
        let picks = [];
        if (this.addressTab === 'client') {
            picks = this.clientList
                .filter(c => this.checkedClient.has(c.seq))
                .map(c => ({ name: c.cname || '', phone: c.mobile || '' }));
        } else {
            picks = this.userList
                .filter(u => this.checkedUser.has(u.ubName + '|' + u.ubTel))
                .map(u => ({ name: u.ubName || '', phone: u.ubTel || '' }));
        }
        if (picks.length === 0) {
            this.toastWarn('선택된 항목이 없습니다.');
            return;
        }
        // rawInput 끝에 append — parseInput 이 중복/유효성 처리
        const lines = picks.map(p => `${p.name}|${p.phone}`).join('\n');
        this.rawInput = (this.rawInput ? this.rawInput.trim() + '\n' : '') + lines;
        this.parseInput();

        // 체크 초기화
        if (this.addressTab === 'client') this.checkedClient = new Set();
        else this.checkedUser = new Set();
    };

    return o;
};
